import useTranslation from '@hooks/useTranslation';

import type { IEngagementStatsFacility } from '@pages/SiteEngagement/model/engagementStatsFacility';

import style from '../components/SiteEngagementMetrics/style.module.scss';
import { getSiteEngagementSeriesData } from '../helpers/getSiteEngagementMetricsSeriesData';
import { selectSiteEngagementMetricsFilterStorePersistState } from '../store/useSiteEngagementMetricsFilterStorePersist';

export const FACILITY_NAME_TOOLTIP_ID = 'engagement-metrics-facility-name-tooltip';

export const useGetSiteEngagementOptions = (data?: IEngagementStatsFacility) => {
  const { t } = useTranslation();

  const filter = selectSiteEngagementMetricsFilterStorePersistState();

  const { age, timeAwaiting, siteResponse, initialResponse, max, min } = getSiteEngagementSeriesData(data);

  const chartHeight = (data?.facilities.length || 0) * (4 * 26);
  const itemCount = data?.facilities.length || 0;
  const isMoreThanFourItems = itemCount > 4;
  const metricTypeSuffix = filter.metricType.charAt(0);

  const baseOptions: Highcharts.Options = {
    title: { text: '' },
    credits: { enabled: false },
    accessibility: { enabled: false },
    legend: {
      symbolRadius: 0,
      align: 'center',
      useHTML: true,
      className: style.legend,
    },
    plotOptions: {
      bar: {
        pointWidth: 13,
        borderRadius: 0,
      },
      series: {
        dataLabels: {
          enabled: true,
          useHTML: true,
          className: style.dataLabels,
          formatter: function () {
            return this.y ? `${this.y}${metricTypeSuffix}` : '';
          },
        },
      },
    },
    xAxis: {
      type: 'category',
      labels: {
        useHTML: true,
        reserveSpace: true,
        formatter: function () {
          return `<span data-tooltip-id="${FACILITY_NAME_TOOLTIP_ID}" data-tooltip-content="${this.value}">${this.value}</span>`;
        },
      },
      className: style.xAxisLabel,
      tickLength: 0,
    },
    yAxis: {
      min,
      max,
      title: { text: '' },
      className: style.yAxisLabel,
      labels: {
        useHTML: true,
        formatter: function () {
          return `${this.value}${metricTypeSuffix}`;
        },
      },
    },
    tooltip: {
      useHTML: true,
      outside: true,
      enabled: true,
      formatter: function () {
        const { point, series } = this;
        return `<b>${point.y} ${t(filter.metricType)}</b> ${series.name}`;
      },
    },
    series: [
      {
        name: t('Age'),
        data: age,
        type: 'bar',
        color: '#BFE2EC',
      },
      {
        name: t('Time Awaiting Site Response'),
        data: timeAwaiting,
        type: 'bar',
        color: '#55BBD0',
      },
      {
        name: t('Site response'),
        data: siteResponse,
        type: 'bar',
        color: '#3C97AA',
      },
      {
        name: t('Initial response'),
        data: initialResponse,
        type: 'bar',
        color: '#2F6F83',
      },
    ],
  };

  const hiddenOptions: Highcharts.Options = {
    chart: {
      height: isMoreThanFourItems ? chartHeight : 'auto',
    },
    ...baseOptions,
  };

  const options: Highcharts.Options = {
    ...baseOptions,
    chart: {
      type: 'bar',
      scrollablePlotArea: {
        minHeight: chartHeight,
        opacity: 1,
      },
      marginRight: 15,
    },
    xAxis: {
      ...baseOptions.xAxis,
    },
  };

  return { hiddenOptions, options };
};
